/**
 * Scopes whose feature is only surfaced when the tenant is entitled to a
 * product. Replaces the superuser-only rollout gate that parked Merchants:
 * the same declarative single-set shape, keyed on entitlement instead of role,
 * so adding a scope here withdraws it from the sidebar and the route guard at
 * once.
 *
 * A portal-side gate, not enforcement — the backend still serves these
 * endpoints to any principal holding the grant.
 */
import { PRODUCTS, hasProduct, type ProductCode, type TenantProducts } from './products';

/** Back-compat re-export for existing importers — `@/lib/auth/products` is canonical. */
export type { TenantProducts };

export const SCOPE_PRODUCTS: Readonly<Record<string, ProductCode>> = {
  // F10 §§97–105: merchant credit is the `pay_in_installments` product.
  merchants: PRODUCTS.payInInstallments,
};

/** The product `scope` requires, or `undefined` when it is ungated. */
export function requiredProduct(scope: string): ProductCode | undefined {
  return SCOPE_PRODUCTS[scope];
}

/**
 * Whether `scope` is withheld because the tenant lacks its product.
 *
 * `null` (unresolved) fails open here, unlike `hasProduct`'s "no" — the route
 * guard runs in the router's `beforeLoad`, before the resolve query settles,
 * so treating "not known yet" as "not entitled" would bounce a hard refresh
 * of an entitled route to /forbidden. A resolved-but-empty list still fails
 * closed.
 */
export function isProductWithheld(
  scope: string,
  enabledProducts: TenantProducts
): boolean {
  const required = requiredProduct(scope);
  if (!required) return false;
  if (enabledProducts === null) return false;
  return !hasProduct(required, enabledProducts);
}
